import { getApiBaseUrl, handleApiError } from '../../core/config/api';
import { PersonnelConfig, VehiculeConfig, Service, FonctionArmement, Competence, Formation } from '../types/config';

class ConfigService {
  private getAuthToken(): string | null {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    return token;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `Erreur HTTP: ${response.status}`);
    }

    return response;
  }

  // ==================== PERSONNELS ====================

  async getPersonnelConfigs(): Promise<{ success: boolean; data?: PersonnelConfig[]; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/personnel`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async createPersonnelConfig(config: Omit<PersonnelConfig, 'id'>): Promise<{ success: boolean; data?: PersonnelConfig; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/personnel`, {
        method: 'POST',
        body: JSON.stringify(config),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async updatePersonnelConfig(id: number, config: Partial<PersonnelConfig>): Promise<{ success: boolean; data?: PersonnelConfig; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/personnel/${id}`, {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async deletePersonnelConfig(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/personnel/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  // ==================== VÉHICULES ====================

  async getVehiculeConfigs(): Promise<{ success: boolean; data?: VehiculeConfig[]; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/vehicules`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async createVehiculeConfig(config: Omit<VehiculeConfig, 'id'>): Promise<{ success: boolean; data?: VehiculeConfig; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/vehicules`, {
        method: 'POST',
        body: JSON.stringify(config),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async updateVehiculeConfig(id: number, config: Partial<VehiculeConfig>): Promise<{ success: boolean; data?: VehiculeConfig; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/vehicules/${id}`, {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async deleteVehiculeConfig(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/vehicules/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  // ==================== SERVICES ====================

  async getCategoriesSpecialite(): Promise<{ success: boolean; data?: Array<{ id: number; nom: string; id_service: number; ordre: number; actif: number; service_nom?: string }>; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/categories-specialite`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async getServices(): Promise<{ success: boolean; data?: Service[]; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/services`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  // ==================== FONCTIONS ARMEMENT ====================

  async getFonctionsArmement(): Promise<{ success: boolean; data?: FonctionArmement[]; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/fonctions-armement`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async createFonctionArmement(fonctionArmement: Omit<FonctionArmement, 'id'>): Promise<{ success: boolean; data?: FonctionArmement; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/fonctions-armement`, {
        method: 'POST',
        body: JSON.stringify(fonctionArmement),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async updateFonctionArmement(id: number, fonctionArmement: Partial<FonctionArmement>): Promise<{ success: boolean; data?: FonctionArmement; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/fonctions-armement/${id}`, {
        method: 'PUT',
        body: JSON.stringify(fonctionArmement),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async deleteFonctionArmement(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/fonctions-armement/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  // ==================== COMPÉTENCES ====================

  async getCompetences(): Promise<{ success: boolean; data?: Competence[]; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/competences`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async createCompetence(competence: Omit<Competence, 'id'>): Promise<{ success: boolean; data?: Competence; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/competences`, {
        method: 'POST',
        body: JSON.stringify(competence),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async updateCompetence(id: number, competence: Partial<Competence>): Promise<{ success: boolean; data?: Competence; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/competences/${id}`, {
        method: 'PUT',
        body: JSON.stringify(competence),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async deleteCompetence(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/competences/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  // ==================== FORMATIONS ====================

  async getFormations(): Promise<{ success: boolean; data?: Formation[]; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/formations`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async createFormation(formation: Omit<Formation, 'id' | 'competence_nom'>): Promise<{ success: boolean; data?: Formation; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/formations`, {
        method: 'POST',
        body: JSON.stringify(formation),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async updateFormation(id: number, formation: Partial<Formation>): Promise<{ success: boolean; data?: Formation; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/formations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(formation),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  async deleteFormation(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/config/formations/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }
}

const configService = new ConfigService();
export default configService;

